export type ModuleDef = {
  slug: string;
  title: string;
  short: string;
  description: string;
  features: string[];
  businessResult: string;
  lean?: string;
};

export const modules: ModuleDef[] = [
  {
    slug: "mes",
    title: "MES — Manufacturing Execution System",
    short: "Turn plans into shop-floor reality.",
    description:
      "Control production execution from manufacturing orders to shop-floor confirmations. Give operators clear priorities, digital instructions and real-time feedback loops.",
    features: [
      "Manufacturing orders",
      "Job orders",
      "Routings and operations",
      "BOM-based material requirements",
      "Operator confirmations",
      "WIP and finished goods tracking",
      "Scrap and rework",
      "Production genealogy",
      "Shop-floor dashboards",
    ],
    businessResult:
      "Better execution discipline, less manual reporting, faster order status visibility.",
    lean: "Standardized work, one-piece flow visibility, digital Kanban signals, takt vs actual.",
  },
  {
    slug: "machine-monitoring",
    title: "Machine Monitoring & IIoT",
    short: "Connect any machine. See what really happens.",
    description:
      "Connect old and new machines to collect real production signals automatically.",
    features: [
      "Run / stop status",
      "Speed and output",
      "Micro-stops",
      "Machine hours",
      "Process parameters where available",
      "Live machine status boards",
      "Alerts and event history",
    ],
    businessResult: "No more guessing. See where capacity is lost.",
    lean: "Visual management, TPM baseline, waste identification (waiting, motion, defects).",
  },
  {
    slug: "oee-downtime",
    title: "OEE & Downtime Management",
    short: "Real-time OEE with reasons that lead to action.",
    description:
      "Measure availability, performance and quality in real time. Classify downtime reasons and focus improvement actions on the biggest losses.",
    features: [
      "Real-time OEE",
      "Availability, performance, quality",
      "Downtime reasons",
      "Planned vs unplanned stops",
      "Speed losses",
      "Shift reports",
      "Loss trees",
      "Improvement dashboards",
    ],
    businessResult: "Increase OEE and reduce unplanned downtime.",
    lean: "Pareto of the Six Big Losses, Kaizen priorities, A3 problem-solving data.",
  },
  {
    slug: "material-flow",
    title: "Material Flow Control",
    short: "Move materials without paper, without chaos.",
    description:
      "Control material movement between warehouse, production, WIP and finished goods.",
    features: [
      "Material issue to job orders",
      "Consumption confirmation",
      "Transfer requests",
      "WIP tracking",
      "Batch tracking",
      "Stock location visibility",
      "Actual vs normative consumption",
    ],
    businessResult: "Reduce material chaos, improve traceability and avoid production delays.",
    lean: "Pull system, digital Kanban, JIT replenishment, waste of inventory.",
  },
  {
    slug: "qr-traceability",
    title: "QR Coding & Traceability",
    short: "Every drum, pallet, batch and product — identified.",
    description:
      "Generate and scan QR codes for raw materials, semi-finished goods, finished goods, drums, pallets, containers, batches and components.",
    features: [
      "QR label generation",
      "Industrial label printing",
      "Scan-to-confirm workflow",
      "RM / SFG / FG traceability",
      "Batch genealogy",
      "Drum / pallet / product labels",
      "Traceability reports",
    ],
    businessResult: "Faster identification, fewer mistakes, better production genealogy.",
    lean: "Poka-yoke (mistake-proofing), 5S location control, first-in-first-out discipline.",
  },
  {
    slug: "quality-management",
    title: "Quality Management",
    short: "Digitize checks. Link results to orders, batches and operators.",
    description:
      "Digitize quality checks and connect inspection results directly to orders, machines, batches and operators.",
    features: [
      "Digital quality checklists",
      "In-process inspections",
      "Final inspections",
      "Nonconformance tracking",
      "Photo and document attachments",
      "Quality hold / release logic",
      "Quality dashboards",
    ],
    businessResult: "Improve quality discipline and reduce hidden defects.",
    lean: "Six Sigma DMAIC data, cost of poor quality (COPQ), jidoka (stop-at-defect).",
  },
  {
    slug: "job-orders",
    title: "Job Orders Management",
    short: "Digital job orders on every operator tablet.",
    description:
      "Turn plans into executable shop-floor tasks with clear priorities and digital operator workflow.",
    features: [
      "Job order creation",
      "Operator assignment",
      "Work instructions",
      "Attachments and drawings",
      "Target quantities",
      "Output confirmation",
      "Scrap confirmation",
      "Status tracking",
      "Priority changes",
    ],
    businessResult: "Better production coordination and real-time order status.",
    lean: "Standardized work instructions, leader standard work, heijunka execution.",
  },
  {
    slug: "cmms-maintenance",
    title: "CMMS / Maintenance",
    short: "Connect downtime with maintenance action.",
    description:
      "Connect production losses with maintenance execution. Track machine hours, create work requests and manage preventive maintenance.",
    features: [
      "Digital work requests",
      "Preventive maintenance",
      "Corrective maintenance",
      "Machine hour tracking",
      "Maintenance calendar",
      "Spare parts links",
      "Downtime-linked maintenance tasks",
      "Technician workflow",
    ],
    businessResult: "Reduce unplanned failures and improve maintenance response.",
    lean: "TPM autonomous & planned maintenance, MTBF/MTTR, reliability-centered improvement.",
  },
  {
    slug: "andon",
    title: "ANDON",
    short: "Call for help. Escalate. Resolve. Learn.",
    description:
      "Give operators a fast way to call for help and escalate production, quality, material or maintenance issues.",
    features: [
      "Operator call buttons",
      "Escalation rules",
      "Live Andon boards",
      "Response time tracking",
      "Issue categories",
      "Notifications",
      "Resolution history",
    ],
    businessResult: "Faster reaction to problems before they become expensive downtime.",
    lean: "Toyota-style Andon, jidoka, real-time escalation, respect-for-people culture.",
  },
  {
    slug: "energy-management",
    title: "Energy Management — ISO 50001 Ready",
    short: "Track energy per machine, order, batch, product.",
    description:
      "Track energy consumption at machine, line, order, batch, product or shift level. Support ISO 50001 energy management processes with real production-based energy KPIs.",
    features: [
      "Energy per machine",
      "Energy per order",
      "Energy per batch",
      "Energy per unit, meter or kilogram",
      "Idle energy losses",
      "Peak load visibility",
      "Energy baselines",
      "Alerts and dashboards",
      "Sustainability reporting support",
    ],
    businessResult: "Reduce energy waste and understand the real energy cost of production.",
    lean: "Green Lean, waste-of-energy (Muda), ISO 50001 energy performance improvement.",
  },
  {
    slug: "dashboards-ai",
    title: "Dashboards & AI Insights",
    short: "Facts, not opinions. For every role.",
    description:
      "Turn factory data into decisions for operators, supervisors, plant managers and executives.",
    features: [
      "Role-based dashboards",
      "Production KPIs",
      "OEE analysis",
      "Downtime analysis",
      "Energy analytics",
      "Maintenance analytics",
      "Quality analytics",
      "AI-supported loss analysis",
      "Recommendations for improvement",
    ],
    businessResult: "Faster decisions based on reliable production facts.",
    lean: "Continuous improvement dashboards, Hoshin Kanri tracking, gemba reviews backed by data.",
  },
  {
    slug: "tools-management",
    title: "Tools Management",
    short: "Control every tool. Reduce manufacturing costs.",
    description:
      "Track industrial tools and drawing dies through their lifecycle, connect tool life with real production output and plan replacement before wear affects quality, material consumption or uptime.",
    features: [
      "Digital tool library",
      "Tool sets and die sequences",
      "QR tool identification",
      "Production-based tool life",
      "Inspection and rework history",
      "Tool inventory and shortages",
      "Replacement forecasting",
      "ERP purchasing visibility",
    ],
    businessResult: "Fewer unnecessary replacements, fewer tool-related stops and lower material losses.",
    lean: "TPM for tooling, Kaizen on tool-related losses, standard work for tool changes.",
  },
];

export const moduleBySlug = (slug: string) => modules.find((m) => m.slug === slug);